/**
 * Helpers that make live updates SILENT (Registry page, operator report
 * "the page periodically self-refreshes and it is super annoying"):
 *
 *  - shareStructure: a refetch that returns the same data keeps the SAME
 *    object identities, so React state does not change and nothing
 *    re-renders; when part of the data changed only that part is new;
 *  - isUserBusy / whenQuiet: background refreshes wait while the tab is
 *    hidden, text is selected, a form field has focus or the user is
 *    scrolling, then run once (coalesced) when it is quiet again;
 *  - stableOrder: a list sorted by "last updated" keeps its rows where they
 *    are while updates stream in (new rows still appear at the top);
 *  - a tiny cell store, so a value that changes on every refresh (the
 *    "updated 3s ago" stamp) re-renders only its own component.
 */

/** `next` with every subtree deep-equal to `prev` replaced by the `prev`
 *  object itself (so `shareStructure(a, b) === a` when nothing changed). */
export function shareStructure<T>(prev: unknown, next: T): T {
  if (prev === next) return next;
  if (
    prev === null ||
    next === null ||
    typeof prev !== "object" ||
    typeof next !== "object"
  )
    return next;
  if (Array.isArray(prev) !== Array.isArray(next)) return next;
  // Only plain objects and arrays are compared structurally (a Map or Date
  // has no own enumerable keys and would compare equal to anything).
  if (!Array.isArray(next) && Object.getPrototypeOf(next) !== Object.prototype)
    return next;
  if (!Array.isArray(prev) && Object.getPrototypeOf(prev) !== Object.prototype)
    return next;
  if (Array.isArray(next)) {
    const p = prev as unknown[];
    const out = (next as unknown[]).map((x, i) =>
      i < p.length ? shareStructure(p[i], x) : x,
    );
    const same = out.length === p.length && out.every((x, i) => x === p[i]);
    return (same ? p : out) as T;
  }
  const p = prev as Record<string, unknown>;
  const n = next as Record<string, unknown>;
  const keys = Object.keys(n);
  const out: Record<string, unknown> = {};
  let same = keys.length === Object.keys(p).length;
  for (const k of keys) {
    out[k] = shareStructure(p[k], n[k]);
    if (out[k] !== p[k] || !(k in p)) same = false;
  }
  return (same ? p : out) as T;
}

/** shareStructure for a Map: `prev` itself when every entry is unchanged. */
export function shareMap<K, V, M extends ReadonlyMap<K, V>>(
  prev: M,
  next: M,
): M {
  const out = new Map<K, V>();
  let same = prev.size === next.size;
  for (const [k, v] of next) {
    const s = prev.has(k) ? shareStructure(prev.get(k), v) : v;
    out.set(k, s);
    if (!prev.has(k) || prev.get(k) !== s) same = false;
  }
  return (same ? prev : out) as unknown as M;
}

// ── "is the user busy?" ───────────────────────────────────────────────────
let lastScrollAt = 0;
let listening = false;
const quietListeners = new Set<() => void>();

function ensureListeners(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  const wake = () => {
    for (const l of [...quietListeners]) l();
  };
  window.addEventListener(
    "scroll",
    () => {
      lastScrollAt = Date.now();
    },
    { capture: true, passive: true },
  );
  document.addEventListener("visibilitychange", wake);
  document.addEventListener("selectionchange", wake);
  document.addEventListener("focusout", () => setTimeout(wake, 0));
  window.addEventListener("pointerup", () => setTimeout(wake, 0));
}

const FIELD = /^(INPUT|TEXTAREA|SELECT)$/;

/** True while a background refresh would be disruptive: the tab is hidden,
 *  text is selected, a form field has focus, or the user scrolled within the
 *  last second. */
export function isUserBusy(now: number = Date.now()): boolean {
  if (typeof document === "undefined") return false;
  ensureListeners();
  if (document.hidden) return true;
  const a = document.activeElement;
  if (a && FIELD.test(a.tagName)) return true;
  const sel = typeof getSelection === "function" ? getSelection() : null;
  if (sel && !sel.isCollapsed && sel.toString().trim() !== "") return true;
  return now - lastScrollAt < 1000;
}

/** Wraps `fn` so that calls are coalesced (`debounceMs`) and deferred while
 *  `isUserBusy()`; the deferred call runs as soon as it is quiet again (or
 *  after `maxWaitMs` of busyness, so data never goes stale for good).
 *  `now: true` calls (a click on Refresh) bypass all of it. */
export function quietly(
  fn: () => void,
  opts: { debounceMs?: number; maxWaitMs?: number } = {},
): { (): void; now(): void; cancel(): void } {
  const debounceMs = opts.debounceMs ?? 400;
  const maxWaitMs = opts.maxWaitMs ?? 60_000;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pendingSince = 0;
  let cancelled = false;
  const wake = () => {
    if (pendingSince && !isUserBusy()) schedule(0);
  };
  const run = () => {
    timer = undefined;
    if (cancelled || !pendingSince) return;
    if (isUserBusy() && Date.now() - pendingSince < maxWaitMs) {
      quietListeners.add(wake);
      timer = setTimeout(run, 2_000);
      return;
    }
    pendingSince = 0;
    quietListeners.delete(wake);
    fn();
  };
  const schedule = (ms: number) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, ms);
  };
  const call = () => {
    if (cancelled) return;
    if (!pendingSince) pendingSince = Date.now();
    schedule(debounceMs);
  };
  call.now = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    pendingSince = 0;
    quietListeners.delete(wake);
    if (!cancelled) fn();
  };
  call.cancel = () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    quietListeners.delete(wake);
  };
  return call;
}

// ── stable list order ─────────────────────────────────────────────────────
/** Orders `items` newest-first by `updatedAt` the FIRST time a key is seen
 *  and then keeps every known row where it is (`ranks` persists across
 *  renders); rows seen for the first time go to the top. A row that merely
 *  got updated does not jump. */
export function stableOrder<T>(
  items: readonly T[],
  keyOf: (t: T) => string,
  updatedAt: (t: T) => number,
  ranks: Map<string, number>,
): T[] {
  const fresh = items
    .filter((t) => !ranks.has(keyOf(t)))
    .sort((a, b) => updatedAt(b) - updatedAt(a));
  let low = Math.min(0, ...ranks.values());
  // lowest rank first: assign newest-first rows ascending below the minimum
  for (let i = fresh.length - 1; i >= 0; i--)
    ranks.set(keyOf(fresh[i]!), --low);
  return [...items].sort((a, b) => ranks.get(keyOf(a))! - ranks.get(keyOf(b))!);
}

// ── a value with its own subscribers ──────────────────────────────────────
export interface Cell<T> {
  get(): T;
  set(v: T): void;
  subscribe(cb: () => void): () => void;
}

export function createCell<T>(initial: T): Cell<T> {
  let value = initial;
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set(v) {
      if (v === value) return;
      value = v;
      for (const s of [...subs]) s();
    },
    subscribe(cb) {
      subs.add(cb);
      return () => subs.delete(cb);
    },
  };
}
